import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePickerField } from '../components/DatePickerField';
import { ArrowLeft, Download } from 'lucide-react';
import { format } from 'date-fns';
import { downloadAuthedBlob } from '../lib/download';

const fmt = (n) => new Intl.NumberFormat('en-KE', { maximumFractionDigits: 2 }).format(Number(n || 0));

export default function CommodityStatement() {
  const { commodityId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ start_date: null, end_date: null, factory: 'All', move_type: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = format(filters.start_date, 'yyyy-MM-dd');
      if (filters.end_date) params.end_date = format(filters.end_date, 'yyyy-MM-dd');
      if (filters.factory && filters.factory !== 'All') params.factory = filters.factory;
      if (filters.move_type) params.move_type = filters.move_type;
      const r = await api.get(`/commodity-inventory/${commodityId}/statement`, { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [commodityId, filters]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    const params = {};
    if (filters.start_date) params.start_date = format(filters.start_date, 'yyyy-MM-dd');
    if (filters.end_date) params.end_date = format(filters.end_date, 'yyyy-MM-dd');
    if (filters.factory && filters.factory !== 'All') params.factory = filters.factory;
    if (filters.move_type) params.move_type = filters.move_type;
    const safe = (data?.item?.name || 'Commodity').replace(/[^A-Za-z0-9_-]/g, '_');
    await downloadAuthedBlob(`/commodity-inventory/${commodityId}/statement/pdf`, `Commodity_Statement_${safe}.pdf`, { params });
  };

  if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]" /></div>;

  return (
    <div className="space-y-6" data-testid="commodity-statement-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/commodity-inventory"><Button variant="ghost" size="sm" className="gap-1"><ArrowLeft className="h-4 w-4" />Back</Button></Link>
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#1B4D3E]" data-testid="commodity-name">{data.item.name}</h1>
            <p className="text-xs text-slate-500 mt-1">Unit: {data.item.unit} · Reorder Level: {data.item.reorder_level || '—'}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-commodity-pdf" className="gap-1"><Download className="h-4 w-4" />Download PDF</Button>
      </div>

      <Card><CardContent className="pt-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2"><Label className="text-xs">From:</Label><DatePickerField date={filters.start_date} onDateChange={(d) => setFilters({ ...filters, start_date: d })} buttonClassName="w-36 text-sm" placeholder="Start" /></div>
          <div className="flex items-center gap-2"><Label className="text-xs">To:</Label><DatePickerField date={filters.end_date} onDateChange={(d) => setFilters({ ...filters, end_date: d })} buttonClassName="w-36 text-sm" placeholder="End" /></div>
          <div className="flex items-center gap-2"><Label className="text-xs">Factory:</Label>
            <Select value={filters.factory} onValueChange={(v) => setFilters({ ...filters, factory: v })}>
              <SelectTrigger className="w-36 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="All">All</SelectItem><SelectItem value="Ndumberi">Ndumberi</SelectItem><SelectItem value="Githunguri">Githunguri</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2"><Label className="text-xs">Type:</Label>
            <Select value={filters.move_type || 'all'} onValueChange={(v) => setFilters({ ...filters, move_type: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-40 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="opening">Opening</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="adjustment">Adjustment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Opening Balance" value={fmt(data.opening_balance)} />
        <SummaryCard label="Total In" value={fmt(data.total_in)} tone="text-emerald-700" />
        <SummaryCard label="Total Out" value={fmt(data.total_out)} tone="text-red-700" />
        <SummaryCard label="Closing Balance" value={fmt(data.closing_balance)} tone="font-semibold" testid="closing-balance" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Movements</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead>
              <TableHead>Factory</TableHead>
              <TableHead className="text-right">In</TableHead><TableHead className="text-right">Out</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.movements.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400 italic">No movements in range.</TableCell></TableRow> : data.movements.map(m => (
                <TableRow key={m.id} data-testid={`movement-${m.id}`}>
                  <TableCell>{m.date}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{m.type}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[280px] truncate">{m.reference_label || '-'}</TableCell>
                  <TableCell className="text-xs">{m.factory || '-'}</TableCell>
                  <TableCell className="text-right text-emerald-700">{m.direction === 'in' ? fmt(m.quantity) : '-'}</TableCell>
                  <TableCell className="text-right text-red-700">{m.direction === 'out' ? fmt(m.quantity) : '-'}</TableCell>
                  <TableCell className="text-right font-medium">{fmt(m.running_balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, tone = '', testid }) {
  return (<Card><CardContent className="p-3"><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className={`text-lg mt-1 ${tone}`} data-testid={testid}>{value}</div></CardContent></Card>);
}
